const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const getAuthToken = (): string | null => {
  return localStorage.getItem("session_token");
};

type ApiResponse<T = any> = T;

export const publicRequest = async <T = any>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  data?: any
): Promise<ApiResponse<T>> => {
  const url = `${apiBaseUrl}${endpoint}`;
  const options: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
    },
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Network response was not ok");
    }

    return (await response.json()) as ApiResponse<T>;
  } catch (error) {
    console.error("API request failed:", error);
    throw error;
  }
};

export const privateRequest = async <T = any>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
  data?: any
): Promise<ApiResponse<T>> => {
  const url = `${apiBaseUrl}${endpoint}`;
  const token = getAuthToken();

  const options: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  if (data) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      // Handle 401/403 responses for session expiration
      if (response.status === 401) {
        // the Layout component can listens for this event
        const sessionExpiredEvent = new CustomEvent('sessionExpired');
        window.dispatchEvent(sessionExpiredEvent);
        localStorage.removeItem("session_token");
      } else if (response.status === 403) {
        const insufficientPermissionsEvent = new CustomEvent('insufficientPermissions');
        window.dispatchEvent(insufficientPermissionsEvent);
      }
      
      const errorData = await response.json();
      throw new Error(errorData.message || "Network response was not ok");
    }

    return (await response.json()) as ApiResponse<T>;
  } catch (error) {
    console.error("Private API request failed:", error);
    throw error;
  }
};

export const multipartRequest = async <T = any>(
  endpoint: string,
  method: "POST" | "PUT" = "POST",
  data?: FormData
): Promise<ApiResponse<T>> => {
  const url = `${apiBaseUrl}${endpoint}`;
  const token = getAuthToken();

  const options: RequestInit = {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: data,
  };

  try {
    const response = await fetch(url, options);
    if (!response.ok) {
      // Handle 401/403 responses for session expiration
      if (response.status === 401 || response.status === 403) {
        // Dispatch a custom event that the Layout component can listen to
        const sessionExpiredEvent = new CustomEvent('sessionExpired');
        window.dispatchEvent(sessionExpiredEvent);
        
        // Clear the token from localStorage
        localStorage.removeItem("session_token");
      }
      
      const errorData = await response.json();
      throw new Error(errorData.message || "Upload failed");
    }

    return (await response.json()) as ApiResponse<T>;
  } catch (error) {
    console.error("Video upload error:", error);
    throw error;
  }
};


export interface UpdateApplicantPayload {
  positions?: string[];
  rating?: number;
  applicationStatus?: string;
  videoUuid?: string;
  resumeDocumentId?: string;
  summary?: string;
  isGloballyVisible?: boolean;
  isActive?: boolean;
  lastActiveAt?: string;
}

export interface UpsertMediaPayload {
  id?: string;
  original_file_key?: string;
  original_file_mime_type?: string;
  upload_size_kb?: number;
  upload_pct_complete?: number;
  upload_avg_kbps?: number;
  metadata?: {
    metadata_version?: any;
    source?: any;
    upload_device?: {
      name?: any;
      manufacturer?: any;
      model?: any;
      version?: any;
      os?: any;
      os_version?: any;
    };
  };
}

// API endpoints
export const getIntroVideo = async (id: string | number) => {
  return await publicRequest(`employer/${id}`, "GET");
};

export const employerSignUp = async (
  name: string,
  companyName: string,
  phoneNo: string
) => {
  return await publicRequest(`employer/signup`, "POST", {
    name,
    companyName,
    phoneNo,
  });
};

export const applicantSignUp = async (payload: any) => {
  return await publicRequest(`applicant/signup`, "POST", payload);
};

export const adminSignIn = async (
  email: string,
  password: string
) => {
  return await publicRequest(`admin/login`, "POST", {
    email,
    password,
  });
};

export const getAllApplicants = async ({
  zipcode,
  globalPositionIds,
  submittedToEmployerIds,
  submittedToEmployerPositionIds,
  currentEmployerId,
  page,
  per_page,
  isGloballyVisible,
  forcePrivate = false
}: {
  zipcode?: string,
  globalPositionIds?: string[],
  submittedToEmployerIds?: string[],
  submittedToEmployerPositionIds?: string[],
  currentEmployerId?: string,
  page?: number,
  per_page?: number,
  isGloballyVisible?: boolean,
  forcePrivate?: boolean
}) => {
  let url = `employer/applicants`;
  let params = [];
  if (zipcode) params.push(`zipCode=${zipcode}`);
  if (globalPositionIds) params.push(`globalPositionIds=${globalPositionIds.join(',')}`);
  if (submittedToEmployerIds) params.push(`submittedToEmployerIds=${submittedToEmployerIds.join(',')}`);
  if (submittedToEmployerPositionIds) params.push(`submittedToEmployerPositionIds=${submittedToEmployerPositionIds.join(',')}`);
  if (currentEmployerId) params.push(`currentEmployerId=${currentEmployerId}`);
  if (page !== undefined) params.push(`page=${page}`);
  if (per_page !== undefined) params.push(`per_page=${per_page}`);
  if (isGloballyVisible !== undefined) params.push(`isGloballyVisible=${isGloballyVisible}`);
  if (params.length > 0) url += `?${params.join("&")}`;
  return await (forcePrivate ? privateRequest : publicRequest)(url, "GET");
};

export const getPaymentSecret = async (payload: any) => {
  return await publicRequest(`payment/create-payment-intent`, "POST", payload);
};

export const getSetupIntent = async (payload: any) => {
  return await privateRequest(`payment/setup-intent`, "POST", payload);
};

export const getSubscriptionPaymentIntent = async (employerId: string) => {
  return await privateRequest(`employers/${employerId}/subscription-payment-intent`, "POST", {});
};

export const sendOtp = async (payload: any) => {
  return await publicRequest("otp/send-otp", "POST", payload);
};

export const verifyOtp = async (payload: any) => {
  return await publicRequest("otp/verify-otp", "POST", payload);
};

export const resendOtp = async (payload: any) => {
  return await publicRequest("otp/resend-otp", "POST", payload);
};

export const getDashboardData = async () => {
  return await privateRequest(`admin/dashboard`, "GET");
};

export const getAllApplicantsData = async (payload: any) => {
  const params = new URLSearchParams();
  
  // Add all parameters to URLSearchParams, handling null/undefined values
  if (payload.page) params.append('page', payload.page);
  if (payload.limit) params.append('limit', payload.limit);
  if (payload.lastActiveStartUtc) params.append('lastActiveStartUtc', payload.lastActiveStartUtc);
  if (payload.lastActiveEndUtc) params.append('lastActiveEndUtc', payload.lastActiveEndUtc);
  if (payload.zipCode) params.append('zipCode', payload.zipCode);
  if (payload.positions && payload.positions.length > 0) params.append('positions', payload.positions);
  if (payload.submittedToEmployerPositionIds && payload.submittedToEmployerPositionIds.length > 0) params.append('submittedToEmployerPositionIds', payload.submittedToEmployerPositionIds.join(','));
  if (payload.verifiedPhone !== null && payload.verifiedPhone !== undefined) params.append('verifiedPhone', payload.verifiedPhone);
  if (payload.hasVideo !== null && payload.hasVideo !== undefined) params.append('hasVideo', payload.hasVideo);
  if (payload.hasResume !== null && payload.hasResume !== undefined) params.append('hasResume', payload.hasResume);
  if (payload.superApplied !== null && payload.superApplied !== undefined) params.append('superApplied', payload.superApplied);
  if (payload.minRating !== null && payload.minRating !== undefined) params.append('minRating', payload.minRating.toString());
  if (payload.maxRating !== null && payload.maxRating !== undefined) params.append('maxRating', payload.maxRating.toString());
  if (payload.isActive !== null && payload.isActive !== undefined) params.append('isActive', payload.isActive);
  if (payload.isPublished !== null && payload.isPublished !== undefined) params.append('isPublished', payload.isPublished);
  if (payload.isGloballyVisible !== null && payload.isGloballyVisible !== undefined) params.append('isGloballyVisible', payload.isGloballyVisible);
  if (payload.fullNameLike) params.append('fullNameLike', payload.fullNameLike);
  if (payload.sort) params.append('sort', payload.sort);
  if (payload.userUuid) params.append('userUuid', payload.userUuid);
  
  return await privateRequest(`admin/applicants?${params.toString()}`, "GET");
};

export const updateApplicant = async (
  applicantId: string | number,
  payload: UpdateApplicantPayload
) => {
  return await privateRequest(
    `admin/applicants/${applicantId}`,
    "PUT",
    payload
  );
};

export const generateApplicantSummary = async (
  userUuid: string,
  payload: { modelName: string; prompt: string }
) => {
  return await privateRequest(
    `admin/applicants/${userUuid}/summary`,
    "POST",
    payload
  );
};

export const sendActiveCheckIn = async (
  userUuid: string,
  payload: { message: string }
) => {
  return await privateRequest(
    `admin/applicants/${userUuid}/send-active-check`,
    "POST",
    payload
  );
};

export const updateApplicantPublic = async (
  applicantId: string | number,
  payload: UpdateApplicantPayload
) => {
  return await publicRequest(
    `applicants/${applicantId}`,
    "PUT",
    payload
  );
};


export const upsertMedia = async (
  mediaId: string,
  payload: UpsertMediaPayload
): Promise<any> => {
  return await publicRequest<any>(`media/${mediaId}`, "PUT", payload);
};

export const updateDocumentMetadata = async (
  documentId: string,
  payload: any
): Promise<any> => {
  return await publicRequest(`documents/${documentId}`, "PUT", payload);
};

export const upsertMeeting = async (
  employerId: string,
  meetingId: string,
  payload: any
) => {
  return await privateRequest(`/employer/${employerId}/meetings/${meetingId}`, 'PUT', payload);
};

export const getEmployerMeetings = async (
  employerId: string,
  params: { 
    page?: number;
    per_page?: number;
    after?: string;
    before?: string;
    sort_by?: string;
    sort_direction?: string;
  }) => {
  const query = new URLSearchParams();
  if (params.page) query.append('page', params.page.toString());
  if (params.per_page) query.append('per_page', params.per_page.toString());
  if (params.after) query.append('after', params.after);
  if (params.before) query.append('before', params.before);
  if (params.sort_by) query.append('sort_by', params.sort_by);
  if (params.sort_direction) query.append('sort_direction', params.sort_direction);
  return await privateRequest(`/employer/${employerId}/meetings?${query.toString()}`, 'GET');
};

export const getEmployerMeeting = async (employerId: string, meetingId: string) => {
  return await privateRequest(`/employer/${employerId}/meetings/${meetingId}`, 'GET');
};


// API functions for employer and position data
export const getEmployer = async (employerId: string) => {
  return await publicRequest(`employers/${employerId}`, "GET");
};

export const getAllEmployers = async (params: {
  page?: number;
  per_page?: number;
  hasActivePositions?: boolean;
} = {}) => {
  const query = new URLSearchParams();
  if (params.page) query.append('page', params.page.toString());
  if (params.per_page) query.append('per_page', params.per_page.toString());
  if (params.hasActivePositions !== undefined) query.append('hasActivePositions', params.hasActivePositions.toString());
  
  const queryString = query.toString();
  return await publicRequest(`employers${queryString ? `?${queryString}` : ''}`, "GET");
};

export const updateEmployer = async (employerId: string, payload: any) => {
  return await privateRequest(`employers/${employerId}`, "PUT", payload);
};

export const getEmployerPosition = async (employerId: string, positionId: string) => {
  return await publicRequest(`employers/${employerId}/positions/${positionId}`, "GET");
};

export const upsertEmployerPosition = async (employerId: string, positionId: string, payload: any) => {
  return await privateRequest(`employers/${employerId}/positions/${positionId}`, "PUT", payload);
};

export const createUser = async (payload: any) => {
  return await publicRequest(`users`, "POST", payload);
};

export const signIn = async (email: string, password: string) => {
  return await publicRequest(`auth/sessions`, "POST", {
    email,
    password,
  });
};

export const getCurrentUser = async () => {
  return await privateRequest(`users/current`, "GET");
};

export const getEmployerPositions = async (
  employerId: string,
  params: { 
    page?: number;
    per_page?: number;
    status?: string;
    sort_by?: string;
    sort_direction?: string;
  } = {},
  forcePrivate = false
) => {
  const query = new URLSearchParams();
  if (params.page) query.append('page', params.page.toString());
  if (params.per_page) query.append('per_page', params.per_page.toString());
  if (params.status) query.append('status', params.status);
  if (params.sort_by) query.append('sort_by', params.sort_by);
  if (params.sort_direction) query.append('sort_direction', params.sort_direction);
  
  return await (forcePrivate ? privateRequest : publicRequest)(`/employer/${employerId}/positions?${query.toString()}`, 'GET');
};

export const getGlobalPositions = async () => {
  return await publicRequest('global_positions');
};

export const deleteEmployerPosition = async (employerId: string, positionId: string) => {
  return await privateRequest(`employers/${employerId}/positions/${positionId}`, "DELETE");
};

export const confirmMeeting = async (applicantMeetingId: string, payload?: { note?: string }) => {
  return await publicRequest(`applicant_meetings/${applicantMeetingId}/confirmation`, "POST", payload);
};

export const declineMeeting = async (applicantMeetingId: string, payload?: { note?: string }) => {
  return await publicRequest(`applicant_meetings/${applicantMeetingId}/declination`, "POST", payload); 
};

export const getApplicants = async (applicantIds: string[], page?: number, per_page?: number) => {
  const idsParam = applicantIds.join(',');
  let url = `applicants?ids=${idsParam}`;
  if (page !== undefined) url += `&page=${page}`;
  if (per_page !== undefined) url += `&per_page=${per_page}`;
  return await publicRequest(url);
};